/**
 * The small text rules the cards share. **Rendering decisions, not data** — the
 * API returns clean strings, and these are the rules most likely to drift
 * between two cards that should read identically.
 */

/** `1 process`, `4 processes`. The plural is `${singular}s` unless given. */
export const countLabel = (count: number, singular: string, plural = `${singular}s`): string =>
  `${String(count)} ${count === 1 ? singular : plural}`;

/**
 * The counts line under a card title — `12 processes · 72 methods`.
 *
 * **A count is omitted when absent and printed when zero.** Absent means the
 * field does not apply to this record's type; zero means genuinely none yet. So
 * a phase with no methods says so, and a method never says "0 processes".
 *
 * The `·` is supplied here rather than arriving in the data.
 */
export const countsLine = (counts: {
  processCount?: number;
  methodCount?: number;
}): string | undefined => {
  const parts = [
    counts.processCount === undefined ? undefined : countLabel(counts.processCount, 'process', 'processes'),
    counts.methodCount === undefined ? undefined : countLabel(counts.methodCount, 'method'),
  ].filter((part): part is string => part !== undefined);

  return parts.length === 0 ? undefined : parts.join(' · ');
};

/**
 * The reference above a card or page title — `L1 · 06`, `METHOD 3.1.1`.
 *
 * **Zero-padded to two digits at L1 only**, which is what the designs show:
 * eleven phases line up as a column of `01`…`11`, where a process number is
 * already dotted (`6.1`) and a bare `1` is correct there.
 */
export const phaseReference = (number: number): string =>
  `L1 · ${String(number).padStart(2, '0')}`;

/**
 * An entry written as `Label: the rest of it`, split into its two halves. **The
 * colon is a real convention in the content**, confirmed against polaris, and
 * is never drawn.
 *
 * **An entry with no colon becomes a label with no description**, not the
 * reverse: a section whose content has not adopted the convention degrades to a
 * plain list rather than to a column of blank headings.
 */
export const splitLabelled = (entry: string): { label: string; description?: string } => {
  const colon = entry.indexOf(':');

  if (colon === -1) {
    return { label: entry.trim() };
  }

  const description = entry.slice(colon + 1).trim();

  return {
    label: entry.slice(0, colon).trim(),
    ...(description === '' ? {} : { description }),
  };
};

/**
 * The dotted reference for a record anywhere in the tree — `5`, `5.1`, `5.1.1`.
 *
 * **Built from its ancestors' numbers, because a summary only carries its own.**
 * `ContentSummary.number` is a method's position within its process. A page
 * knows the levels above a card and composes the string; a search result has no
 * such page, so it walks its own path — every prefix is a record in the catalog
 * being searched, so they are all present.
 *
 * A missing level is skipped rather than printed as a gap: `5..1` is worse
 * than `5.1`.
 */
export const dottedReference = (path: string, numbers: ReadonlyMap<string, number>): string => {
  const parts: string[] = [];
  let prefix = '';

  for (const segment of path.split('/').filter(Boolean)) {
    prefix = prefix === '' ? segment : `${prefix}/${segment}`;
    const number = numbers.get(prefix);

    if (number !== undefined) {
      parts.push(String(number));
    }
  }

  return parts.join('.');
};
